import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";

import { ChevronDownIcon } from "@heroicons/react/outline";

import { classNames } from "@/utils/strings";
import { useIsCurrentPageNumber } from "../../hooks/useIsCurrentPageNumber";
import { NavigationIcon } from "./NavigationIcon";
import { NavigationLink } from "./NavigationLink";
import { NavigationItemType } from "./types";
import { getItemClassName } from "./utils";

export const DropdownMenuItem = ({
  isCurrentPageName,
  item,
  animatedTitleOnHover = false,
}: {
  isCurrentPageName: boolean;
  item: NavigationItemType;
  animatedTitleOnHover?: boolean;
}) => {
  const isCurrentSubPageName = useIsCurrentPageNumber();

  return (
    <NavigationMenuPrimitive.Item key={item.name}>
      <NavigationLink to={item.href ?? ""}>
        <NavigationMenuPrimitive.Trigger
          className={getItemClassName(
            isCurrentPageName,
            "group relative inline-flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm",
          )}
        >
          <NavigationIcon icon={item.icon} />
          {animatedTitleOnHover ? (
            <>
              <span className="hidden truncate text-xs font-medium text-gray-900 2xl:ml-2 2xl:block 2xl:text-sm">
                {item.name}
              </span>
              <span className="ml-0 block max-w-0 overflow-hidden whitespace-nowrap text-xs font-medium text-gray-900 opacity-0 transition-all duration-200 group-hover:ml-2 group-hover:max-w-[120px] group-hover:opacity-100 group-focus:ml-2 group-focus:max-w-[120px] group-focus:opacity-100 group-data-[state=open]:ml-2 group-data-[state=open]:max-w-[120px] group-data-[state=open]:opacity-100 2xl:hidden">
                {item.name}
              </span>
            </>
          ) : (
            <span className="hidden truncate text-xs font-medium text-gray-900 lg:block 2xl:ml-2 2xl:text-sm">
              {item.name}
            </span>
          )}
          <ChevronDownIcon className="h-4 w-4 text-gray-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </NavigationMenuPrimitive.Trigger>
      </NavigationLink>
      <NavigationMenuPrimitive.Content
        forceMount
        className="absolute left-1/2 top-10 z-[9999] mt-1 flex -translate-x-1/2 flex-col gap-2 rounded-md border border-gray-200 bg-white
                  p-2 shadow-md transition-opacity duration-100
                  ease-in-out data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
      >
        {item.items!.map((subItem) => (
          <NavigationLink to={subItem.href}>
            <NavigationMenuPrimitive.Item
              className={classNames(
                "w-[265px] rounded-md p-2 transition-all duration-100 ease-in-out hover:bg-gray-100",
                getItemClassName(isCurrentSubPageName(subItem)),
              )}
              key={subItem.name}
            >
              <div className="flex items-center gap-2 text-sm">
                {subItem.icon && <NavigationIcon icon={subItem.icon} />}
                <div className="flex flex-col text-gray-900">
                  {subItem.name}
                  {subItem.description && (
                    <p className="text-xs text-gray-500">
                      {subItem.description}
                    </p>
                  )}
                </div>
              </div>
            </NavigationMenuPrimitive.Item>
          </NavigationLink>
        ))}
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
};
